import React from 'react';
import {
  BillingPreviewSport,
  BillingPreviewTeamSeat,
} from '../../types/adminInvoice';
import { formatSubscriptionDate } from '../../utils/admin/formatSubscriptionDate';
import SubscriptionStatusBadge from '../admin/subscription/SubscriptionStatusBadge';
import BillingTeamSeatsHover from './BillingTeamSeatsHover';
import { formatSportPrice, getBlockedReasonMessage } from './invoiceSportTableUtils';

export { formatSportPrice, formatUserUntil, formatUntilDate, getBlockedReasonMessage } from './invoiceSportTableUtils';

interface InvoiceSportTableProps {
  sports: BillingPreviewSport[];
  selectedSportCodes: string[];
  onToggleSport: (sportCode: string) => void;
  isSeatLocked?: boolean;
  teamSeats?: BillingPreviewTeamSeat[];
  seatLimit?: number | null;
}

function generatedByTooltip(generatedBy?: string | null): string {
  const label = generatedBy == null || generatedBy === '' ? '—' : generatedBy;
  return `Generated by: ${label}`;
}

const InvoiceSportTable: React.FC<InvoiceSportTableProps> = ({
  sports,
  selectedSportCodes,
  onToggleSport,
  isSeatLocked = false,
  teamSeats = [],
  seatLimit = null,
}) => {
  if (sports.length === 0) {
    return (
      <div className="text-center p-8 bg-gray-50 rounded-lg text-gray-500">
        No sport subscriptions found for this user.
      </div>
    );
  }

  const showSeatHover = isSeatLocked || sports.some((s) => s.user_status === 'seat_locked');

  return (
    <div className="mb-6 space-y-3">
      {showSeatHover && (
        <div className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900">
          <BillingTeamSeatsHover seats={teamSeats} enabled>
            <span className="font-medium">
              Seat locked
              {seatLimit != null ? ` · limit ${seatLimit}` : ''}
              {teamSeats.length > 0 ? ` · ${teamSeats.length} seated` : ''}
            </span>
          </BillingTeamSeatsHover>
          <span className="ml-2 text-orange-800/80">Hover to see all team seat members</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sport</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trial end</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grace end</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sub end</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Select</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sports.map((sport) => {
              const selectable = sport.invoice_selectable === true;
              const isChecked = selectedSportCodes.includes(sport.sport_code);
              const blockedMessage = getBlockedReasonMessage(sport.blocked_reason);
              const isRowSeatLocked = sport.user_status === 'seat_locked';
              const statusTitle = generatedByTooltip(sport.generated_by);

              return (
                <tr key={sport.sport_code} className={!selectable ? 'bg-gray-50' : undefined}>
                  <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{sport.sport_code}</td>
                  <td className="px-3 py-2" title={statusTitle}>
                    <div className="flex flex-wrap items-center gap-1 cursor-default">
                      {isRowSeatLocked && showSeatHover ? (
                        <BillingTeamSeatsHover seats={teamSeats} enabled>
                          <SubscriptionStatusBadge status={String(sport.user_status)} />
                        </BillingTeamSeatsHover>
                      ) : (
                        <SubscriptionStatusBadge status={String(sport.user_status)} />
                      )}
                      {sport.is_grace ? (
                        <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-800">Grace</span>
                      ) : null}
                      {sport.is_paid ? (
                        <span className="px-1.5 py-0.5 text-[10px] rounded bg-green-100 text-green-800">Paid</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                    {formatSubscriptionDate(sport.user_trial_end)}
                  </td>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                    {formatSubscriptionDate(sport.team_grace_end)}
                  </td>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                    {formatSubscriptionDate(sport.team_subscription_end)}
                  </td>
                  <td className="px-3 py-2 text-gray-900 whitespace-nowrap">{formatSportPrice(sport)}</td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      checked={isChecked}
                      disabled={!selectable}
                      title={!selectable ? blockedMessage : undefined}
                      aria-label={selectable ? `Select ${sport.sport_code}` : blockedMessage}
                      onChange={() => onToggleSport(sport.sport_code)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceSportTable;
